import Seo from '@/components/Seo';
import SiteHeader from '@/components/site/SiteHeader';
import SiteFooter from '@/components/site/SiteFooter';
import Hover from '@/components/site/Hover';
import { css } from '@/components/site/css';
import { CONTACT_MAILTO, CALENDAR_URL } from '@/config/links';

/** Hub — the site homepage. Ported verbatim from the design-locked Hub.dc.html. */
export default function HubPage() {
  return (
    <div
      className="site-shell"
      style={css(
        "font-family:'Hanken Grotesk',system-ui,sans-serif;color:#43434D;background:#ffffff;-webkit-font-smoothing:antialiased;overflow-x:clip"
      )}
    >
      <Seo
        title="F&B Insights — The source-of-truth menu data for food & beverage"
        description="We source what venues actually sell and the real prices they charge: directly, item by item, dated and traceable. Menu intelligence for operators, concessionaires, and beverage alcohol brands."
        path="/"
      />

      <SiteHeader
        linkColor="#43434D"
        bg="rgba(255,255,255,0.9)"
        navLinks={[
          { label: "Who it's for", href: '#serve' },
          { label: 'Why native data', href: '#difference' },
          { label: 'Customers', href: '#proof' },
        ]}
        cta={{ label: 'Get in touch', href: CONTACT_MAILTO, bg: '#E9402B', hoverBg: '#D3341F' }}
      />

      <span id="top" />

      {/* Hero */}
      <section style={css('background:#F6F6F4')}>
        <div
          className="site-hero-grid"
          style={css(
            'max-width:1180px;margin:0 auto;padding:84px 28px 84px;display:grid;grid-template-columns:minmax(0,1.05fr) minmax(0,0.95fr);gap:60px;align-items:center'
          )}
        >
          <div>
            <h1
              style={css(
                "font-family:'Schibsted Grotesk',sans-serif;font-size:clamp(42px,5vw,62px);line-height:1.02;font-weight:800;letter-spacing:-0.025em;margin:0 0 26px;color:#24276C;text-wrap:balance"
              )}
            >
              The source-of-truth menu data for food &amp; beverage.
            </h1>
            <p
              style={css(
                'font-size:20px;line-height:1.5;color:#54545E;margin:0 0 34px;max-width:500px;text-wrap:pretty'
              )}
            >
              We source what venues actually sell and the real prices they charge: directly, item by
              item, dated and traceable. That&rsquo;s the difference between data you can act on and
              data you can only look at.
            </p>
            <div className="site-hero-cta" style={css('display:flex;flex-wrap:wrap;gap:14px')}>
              <Hover
                href="#serve"
                style={css(
                  'display:inline-flex;align-items:center;gap:9px;font-size:16px;font-weight:600;color:#fff;background:#E9402B;padding:15px 26px;border-radius:10px'
                )}
                hoverStyle={css('background:#D3341F')}
              >
                See who we serve
                <span style={css('font-size:18px;line-height:1')}>→</span>
              </Hover>
              <Hover
                href="#difference"
                style={css(
                  'display:inline-flex;align-items:center;font-size:16px;font-weight:600;color:#24276C;background:#fff;border:1.5px solid #D8D6D0;padding:15px 26px;border-radius:10px'
                )}
                hoverStyle={css('border-color:#24276C')}
              >
                Why native data matters
              </Hover>
            </div>
          </div>

          <div style={css('position:relative')}>
            <div
              style={css(
                'position:relative;background:#fff;border:1px solid #ECEBE6;border-radius:18px;box-shadow:0 24px 60px -30px rgba(36,39,108,0.28);padding:26px 26px 22px'
              )}
            >
              <div
                style={css(
                  'display:flex;align-items:center;justify-content:space-between;margin-bottom:18px'
                )}
              >
                <span
                  style={css(
                    "font-family:'Schibsted Grotesk',sans-serif;font-size:15px;font-weight:700;color:#24276C"
                  )}
                >
                  One item, fully sourced
                </span>
                <span
                  style={css(
                    'display:inline-flex;align-items:center;gap:6px;font-size:12px;font-weight:600;color:#12A150;background:#EAF7EF;padding:5px 10px;border-radius:20px'
                  )}
                >
                  <span
                    style={css('width:6px;height:6px;border-radius:50%;background:#12A150')}
                  ></span>
                  Verified
                </span>
              </div>
              <div style={css('display:flex;flex-direction:column;gap:0')}>
                {[
                  ['Item', 'Old Fashioned'],
                  ['Category', 'Cocktails · Spirits'],
                  ['Menu price', '$16.50'],
                  ['Sourced from', 'Dine-in menu'],
                  ['Venue', 'Chicago, IL'],
                  ['Recorded', 'Jul 7, 2026'],
                ].map(([label, value]) => (
                  <div
                    key={label}
                    style={css(
                      'display:flex;justify-content:space-between;align-items:baseline;padding:13px 0;border-top:1px solid #F0EFEB'
                    )}
                  >
                    <span style={css('font-size:14px;color:#8A8A93')}>{label}</span>
                    <span
                      style={css(
                        `font-size:15px;font-weight:${label === 'Menu price' ? '700' : '600'};color:#24276C`
                      )}
                    >
                      {value}
                    </span>
                  </div>
                ))}
              </div>
              <div
                style={css(
                  'margin-top:18px;padding-top:16px;border-top:1px solid #F0EFEB;font-size:13px;color:#8A8A93;display:flex;align-items:center;gap:8px'
                )}
              >
                <span style={css('color:#E9402B;font-weight:700')}>↳</span>
                Traceable to the menu it came from. No markup, no guessing.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who we serve */}
      <section id="serve" style={css('background:#F6F6F4;border-top:1px solid #ECEBE6')}>
        <div style={css('max-width:1180px;margin:0 auto;padding:84px 28px')}>
          <div style={css('max-width:660px;margin-bottom:44px')}>
            <h2
              style={css(
                "font-family:'Schibsted Grotesk',sans-serif;font-size:clamp(32px,3.8vw,44px);line-height:1.05;font-weight:800;letter-spacing:-0.02em;margin:0 0 16px;color:#24276C"
              )}
            >
              Who we serve.
            </h2>
            <p style={css('font-size:18px;line-height:1.55;color:#54545E;margin:0')}>
              The same verifiable menu data answers a different question for each buyer.
            </p>
          </div>
          <div
            style={css(
              'display:grid;grid-template-columns:repeat(auto-fit,minmax(290px,1fr));gap:22px'
            )}
          >
            {/* Operator card */}
            <Hover
              to="/operator"
              style={css(
                'display:flex;flex-direction:column;background:#fff;border:1px solid #E6E4DE;border-top:4px solid #E9402B;border-radius:18px;padding:30px 30px 32px'
              )}
              hoverStyle={css(
                'border-color:#24276C;border-top-color:#E9402B;transform:translateY(-4px)'
              )}
            >
              <div
                style={css(
                  'display:flex;align-items:center;justify-content:space-between;margin-bottom:18px'
                )}
              >
                <span
                  style={css(
                    "font-family:'Schibsted Grotesk',sans-serif;font-size:14px;font-weight:800;color:#CFCDC5"
                  )}
                >
                  01
                </span>
                <span
                  style={css(
                    'display:inline-flex;align-items:center;gap:7px;font-size:12px;font-weight:600;color:#43434D;background:#fff;border:1px solid #DCDAD3;padding:5px 12px;border-radius:20px'
                  )}
                >
                  <span
                    style={css('width:6px;height:6px;border-radius:50%;background:#E9402B')}
                  ></span>
                  Now in pilot
                </span>
              </div>
              <h3
                style={css(
                  "font-family:'Schibsted Grotesk',sans-serif;font-size:24px;font-weight:800;margin:0 0 12px;color:#24276C"
                )}
              >
                Restaurant operators
              </h3>
              <p style={css('font-size:16px;line-height:1.55;color:#54545E;margin:0 0 26px')}>
                Stay competitively priced without sending managers out to scout the market. We do
                the pricing recon for you, on real dine-in data.
              </p>
              <Hover
                as="span"
                style={css(
                  'margin-top:auto;display:inline-flex;align-items:center;justify-content:center;gap:8px;font-size:15px;font-weight:600;color:#fff;background:#E9402B;padding:13px 22px;border-radius:10px;align-self:flex-start'
                )}
                hoverStyle={css('background:#D3341F')}
              >
                Get your menu benchmarked <span style={css('font-size:17px')}>→</span>
              </Hover>
            </Hover>

            {/* Airport card */}
            <Hover
              to="/airport"
              style={css(
                'display:flex;flex-direction:column;background:#fff;border:1px solid #E6E4DE;border-top:4px solid #24276C;border-radius:18px;padding:30px 30px 32px'
              )}
              hoverStyle={css('border-color:#24276C;transform:translateY(-4px)')}
            >
              <div
                style={css(
                  'display:flex;align-items:center;justify-content:space-between;margin-bottom:18px'
                )}
              >
                <span
                  style={css(
                    "font-family:'Schibsted Grotesk',sans-serif;font-size:14px;font-weight:800;color:#CFCDC5"
                  )}
                >
                  02
                </span>
                <span
                  style={css(
                    'display:inline-flex;align-items:center;gap:7px;font-size:12px;font-weight:600;color:#43434D;background:#fff;border:1px solid #DCDAD3;padding:5px 12px;border-radius:20px'
                  )}
                >
                  <span
                    style={css('width:6px;height:6px;border-radius:50%;background:#12A150')}
                  ></span>
                  Available now
                </span>
              </div>
              <h3
                style={css(
                  "font-family:'Schibsted Grotesk',sans-serif;font-size:24px;font-weight:800;margin:0 0 12px;color:#24276C"
                )}
              >
                Airport &amp; venue concessionaires
              </h3>
              <p style={css('font-size:16px;line-height:1.55;color:#54545E;margin:0 0 26px')}>
                Airports, stadiums, convention centers, travel plazas: price to the local street
                market and prove it, with real data across every location.
              </p>
              <span
                style={css(
                  'margin-top:auto;display:inline-flex;align-items:center;gap:8px;font-size:15px;font-weight:600;color:#E9402B;align-self:flex-start'
                )}
              >
                Talk to us <span style={css('font-size:17px')}>→</span>
              </span>
            </Hover>

            {/* Beverage card */}
            <Hover
              to="/beverage"
              style={css(
                'display:flex;flex-direction:column;background:#fff;border:1px solid #E6E4DE;border-top:4px solid #24276C;border-radius:18px;padding:30px 30px 32px'
              )}
              hoverStyle={css('border-color:#24276C;transform:translateY(-4px)')}
            >
              <div
                style={css(
                  'display:flex;align-items:center;justify-content:space-between;margin-bottom:18px'
                )}
              >
                <span
                  style={css(
                    "font-family:'Schibsted Grotesk',sans-serif;font-size:14px;font-weight:800;color:#CFCDC5"
                  )}
                >
                  03
                </span>
                <span
                  style={css(
                    'display:inline-flex;align-items:center;gap:7px;font-size:12px;font-weight:600;color:#43434D;background:#fff;border:1px solid #DCDAD3;padding:5px 12px;border-radius:20px'
                  )}
                >
                  <span
                    style={css('width:6px;height:6px;border-radius:50%;background:#12A150')}
                  ></span>
                  Available now
                </span>
              </div>
              <h3
                style={css(
                  "font-family:'Schibsted Grotesk',sans-serif;font-size:24px;font-weight:800;margin:0 0 12px;color:#24276C"
                )}
              >
                Beverage alcohol suppliers
              </h3>
              <p style={css('font-size:16px;line-height:1.55;color:#54545E;margin:0 0 26px')}>
                Know which venues fit your brand and where the open placements are, scored on
                what&rsquo;s actually on the menu and at what price.
              </p>
              <span
                style={css(
                  'margin-top:auto;display:inline-flex;align-items:center;gap:8px;font-size:15px;font-weight:600;color:#E9402B;align-self:flex-start'
                )}
              >
                Talk to us <span style={css('font-size:17px')}>→</span>
              </span>
            </Hover>
          </div>
        </div>
      </section>

      {/* Why native data */}
      <section
        id="difference"
        style={css(
          'background:#ffffff;border-top:1px solid #ECEBE6;border-bottom:1px solid #ECEBE6'
        )}
      >
        <div style={css('max-width:1180px;margin:0 auto;padding:84px 28px')}>
          <div style={css('max-width:640px;margin-bottom:44px')}>
            <h2
              style={css(
                "font-family:'Schibsted Grotesk',sans-serif;font-size:clamp(32px,3.8vw,44px);line-height:1.05;font-weight:800;letter-spacing:-0.02em;margin:0 0 16px;color:#24276C;text-wrap:balance"
              )}
            >
              Real prices you can stand behind, not marked-up guesses.
            </h2>
            <p style={css('font-size:18px;line-height:1.55;color:#54545E;margin:0')}>
              Most menu data is scraped from third-party sites or pulled from delivery apps, where
              prices are inflated and often left outdated. We collect menus at the source, direct
              from each restaurant&rsquo;s own website, and refresh them monthly, so every figure
              reflects what a guest actually pays.
            </p>
          </div>
          <div
            style={css(
              'display:grid;grid-template-columns:repeat(auto-fit,minmax(300px,1fr));gap:22px'
            )}
          >
            <div
              style={css(
                'background:#fff;border:1px solid #E6E4DE;border-radius:16px;padding:32px 30px'
              )}
            >
              <div
                style={css(
                  'display:inline-flex;align-items:center;gap:8px;font-size:13px;font-weight:700;color:#12A150;background:#EAF7EF;padding:6px 12px;border-radius:20px;margin-bottom:22px'
                )}
              >
                Native &amp; first-party
              </div>
              <h3
                style={css(
                  "font-family:'Schibsted Grotesk',sans-serif;font-size:22px;font-weight:700;margin:0 0 18px;color:#24276C"
                )}
              >
                Data you can act on
              </h3>
              <div style={css('display:flex;flex-direction:column;gap:14px')}>
                {[
                  ['Real dine-in prices.', ' What guests actually pay, not delivery markups.'],
                  ['Item-level detail.', ' Every dish and pour, not category averages.'],
                  [
                    'Dated and traceable.',
                    ' You know when it was recorded and where it came from.',
                  ],
                  ['Standardized.', ' Structured the same way across every venue.'],
                ].map(([strong, rest]) => (
                  <div key={strong} style={css('display:flex;gap:12px;align-items:flex-start')}>
                    <span
                      style={css('color:#12A150;font-weight:700;font-size:16px;line-height:1.5')}
                    >
                      ✓
                    </span>
                    <span style={css('font-size:16px;line-height:1.5;color:#43434D')}>
                      <strong style={css('color:#24276C')}>{strong}</strong>
                      {rest}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <div
              style={css(
                'background:#EFEEEA;border:1px solid #E2E0D9;border-radius:16px;padding:32px 30px'
              )}
            >
              <div
                style={css(
                  'display:inline-flex;align-items:center;gap:8px;font-size:13px;font-weight:700;color:#8A8A93;background:#E4E3DD;padding:6px 12px;border-radius:20px;margin-bottom:22px'
                )}
              >
                Scraped &amp; aggregated
              </div>
              <h3
                style={css(
                  "font-family:'Schibsted Grotesk',sans-serif;font-size:22px;font-weight:700;margin:0 0 18px;color:#7C7C86"
                )}
              >
                Data you can only look at
              </h3>
              <div style={css('display:flex;flex-direction:column;gap:14px')}>
                {[
                  'Delivery-app prices marked up to cover fees.',
                  'Averaged and aggregated, so the item-level truth is lost.',
                  'Undated and unverifiable, with no way to know how stale it is.',
                  'Resold by third parties who never touched the menu.',
                ].map((text) => (
                  <div key={text} style={css('display:flex;gap:12px;align-items:flex-start')}>
                    <span
                      style={css('color:#B6B6BD;font-weight:700;font-size:16px;line-height:1.5')}
                    >
                      ×
                    </span>
                    <span style={css('font-size:16px;line-height:1.5;color:#8A8A93')}>{text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Proof */}
      <section id="proof" style={css('background:#ffffff')}>
        <div style={css('max-width:820px;margin:0 auto;padding:88px 28px;text-align:center')}>
          <h2
            style={css(
              "font-family:'Schibsted Grotesk',sans-serif;font-size:clamp(32px,4vw,48px);line-height:1.06;font-weight:800;letter-spacing:-0.02em;margin:0 auto 22px;color:#24276C;max-width:640px;text-wrap:balance"
            )}
          >
            Serious buyers price against this data.
          </h2>
          <p
            style={css(
              'font-size:19px;line-height:1.55;color:#54545E;margin:0 auto 36px;max-width:600px;text-wrap:pretty'
            )}
          >
            The largest airport concessionaire in the U.S. relies on F&amp;B Insights to set prices
            across its locations, on menu data covering all 50 states. Every price is dine-in,
            dated, and captured at the source.
          </p>
          <div style={css('display:flex;flex-wrap:wrap;gap:14px;justify-content:center')}>
            <Hover
              href={CONTACT_MAILTO}
              style={css(
                'display:inline-flex;align-items:center;gap:9px;font-size:16px;font-weight:600;color:#fff;background:#E9402B;padding:16px 30px;border-radius:10px'
              )}
              hoverStyle={css('background:#D3341F')}
            >
              Get in touch
            </Hover>
            <Hover
              href={CALENDAR_URL}
              style={css(
                'display:inline-flex;align-items:center;font-size:16px;font-weight:600;color:#24276C;background:#fff;border:1.5px solid #D8D6D0;padding:16px 30px;border-radius:10px'
              )}
              hoverStyle={css('border-color:#24276C')}
            >
              Book a call
            </Hover>
          </div>
        </div>
      </section>

      <SiteFooter
        variant="dark"
        navLinks={[
          { label: "Who it's for", href: '#serve' },
          { label: 'Why native data', href: '#difference' },
          { label: 'Contact', href: CONTACT_MAILTO },
        ]}
      />
    </div>
  );
}
